import AddBoxSharpIcon from '@mui/icons-material/AddBoxSharp';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DisabledByDefaultSharpIcon from '@mui/icons-material/DisabledByDefaultSharp';
import { ButtonBase, Stack, Tooltip, Typography } from '@mui/material';
import { SimpleTreeView, TreeItem } from '@mui/x-tree-view';
import {
  ClosedQuestion,
  EffectQuestion,
  OpenQuestion,
} from 'features/lead/lead-management/lead-questionnaire-details/lead-questionnaire-details.types';
import PaiCheckbox from 'features/ui/base-components/pai-checkbox';
import React from 'react';
import { makeStyles } from 'tss-react/mui';

interface Props {
  closedQuestions: ClosedQuestion[];
  selectedClosedQuestions: string[];

  effectQuestions: EffectQuestion[];
  selectedEffectQuestions: string[];

  openQuestions: OpenQuestion[];
  selectedOpenQuestions: string[];

  expanded: string[];

  handleToggleTreeNode(event: any, nodeIds: string[]): void;

  handleOpenAddDimensionDialog(categoryId?: string): (event: React.MouseEvent) => void;

  handleOpenConfirmDeleteLeadCategoryDialog(categoryId: string): (event: React.MouseEvent) => void;

  moveClosedCategory(type: 'down' | 'up', categoryId: string): (event: React.MouseEvent) => void;

  moveCompetence(type: 'down' | 'up', categoryId: string, subCategoryId: string): (event: React.MouseEvent) => void;

  handleSelectionClosedQuestionsChange(id: string): (event: React.ChangeEvent<HTMLInputElement>) => void;

  handleOpenConfirmDeleteLeadQuestionDialog(questionId: string): () => void;

  moveClosedQuestion(type: 'down' | 'up', categoryId: string, subCategoryId: string, questionId: string): (event: React.MouseEvent) => void;

  handleOpenAddQuestionsDialog(subCategoryId: string, categoryId?: string, categorySourceId?: string, subCategorySourceId?: string): () => void;

  moveEffectCategory(type: 'down' | 'up', categoryId: string): (event: React.MouseEvent) => void;

  handleSelectionEffectQuestionsChange(id: string): (event: React.ChangeEvent<HTMLInputElement>) => void;

  moveEffectQuestion(type: 'down' | 'up', subCategoryId: string, questionId: string): (event: React.MouseEvent) => void;

  handleSelectionOpenQuestionsChange(id: string): (event: React.ChangeEvent<HTMLInputElement>) => void;

  moveOpenQuestion(type: 'down' | 'up', questionId: string): (event: React.MouseEvent) => void;

  handleOpenAddNewOpenQuestionsDialog(): void;
}

const LeadQuestionsCreation: React.FC<Props> = ({
  closedQuestions,
  selectedClosedQuestions,
  effectQuestions,
  selectedEffectQuestions,
  openQuestions,
  selectedOpenQuestions,
  expanded,
  handleToggleTreeNode,
  handleOpenAddDimensionDialog,
  handleOpenConfirmDeleteLeadCategoryDialog,
  moveClosedCategory,
  moveCompetence,
  handleSelectionClosedQuestionsChange,
  handleOpenConfirmDeleteLeadQuestionDialog,
  moveClosedQuestion,
  handleOpenAddQuestionsDialog,
  moveEffectCategory,
  handleSelectionEffectQuestionsChange,
  moveEffectQuestion,
  handleSelectionOpenQuestionsChange,
  moveOpenQuestion,
  handleOpenAddNewOpenQuestionsDialog,
}) => {
  const { classes, cx } = useStyles();

  return (
    <div className={classes.questionnaire}>
      {/* open questions */}
      <SimpleTreeView expandedItems={expanded} onExpandedItemsChange={handleToggleTreeNode}>
        {closedQuestions.map((category, idx1) => (
          <TreeItem
            itemId={category.id}
            key={category.id}
            className={classes.treeItem}
            label={
              <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} gap={2}>
                {/* name + add button */}
                <Stack direction={'row'} alignItems={'center'} gap={2}>
                  <Typography className={classes.treeLabel}>{category.name.de}</Typography>
                  <ButtonBase className={classes.addButton} onClick={handleOpenAddDimensionDialog(category.id)}>
                    <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} gap={1}>
                      <AddBoxSharpIcon className={classes.icon} />
                      <Typography className={classes.text}>Dimension</Typography>
                    </Stack>
                  </ButtonBase>
                </Stack>
                {/* delete + move actions */}
                <Stack className={cx(classes.actions, 'actions')} direction={'row'} alignItems={'center'} gap={2} flexWrap={'nowrap'}>
                  <ButtonBase className={classes.deleteButton} onClick={handleOpenConfirmDeleteLeadCategoryDialog(category.id)}>
                    <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} gap={1}>
                      <DisabledByDefaultSharpIcon className={classes.icon} />
                      <Typography className={classes.text}>löschen</Typography>
                    </Stack>
                  </ButtonBase>
                  {idx1 !== 0 && (
                    <Tooltip title={'Kategorie nach Oben verschieben'}>
                      <ButtonBase className={classes.moveButton} onClick={moveClosedCategory('up', category.id)}>
                        <ArrowUpwardIcon className={classes.icon} />
                      </ButtonBase>
                    </Tooltip>
                  )}
                  {idx1 !== closedQuestions.length - 1 && (
                    <Tooltip title={'Kategorie nach Unten verschieben'}>
                      <ButtonBase className={classes.moveButton} onClick={moveClosedCategory('down', category.id)}>
                        <ArrowDownwardIcon className={classes.icon} />
                      </ButtonBase>
                    </Tooltip>
                  )}
                </Stack>
              </Stack>
            }
          >
            {category.subCategories.map((subCategory, idx2) => (
              <TreeItem
                itemId={subCategory.id}
                key={subCategory.id}
                label={
                  <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} gap={2}>
                    {/* name */}
                    <Typography className={classes.treeLabel}>{subCategory.name.de}</Typography>
                    {/* delete move actions */}
                    <Stack className={cx(classes.actions, 'actions')} direction={'row'} alignItems={'center'} gap={2} flexWrap={'nowrap'}>
                      <ButtonBase className={classes.deleteButton} onClick={handleOpenConfirmDeleteLeadCategoryDialog(subCategory.id)}>
                        <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} gap={1}>
                          <DisabledByDefaultSharpIcon className={classes.icon} />
                          <Typography className={classes.text}>löschen</Typography>
                        </Stack>
                      </ButtonBase>
                      {idx2 !== 0 && (
                        <Tooltip title={'Kompetenz nach Oben verschieben'}>
                          <ButtonBase className={classes.moveButton} onClick={moveCompetence('up', category.id, subCategory.id)}>
                            <ArrowUpwardIcon className={classes.icon} />
                          </ButtonBase>
                        </Tooltip>
                      )}
                      {idx2 !== closedQuestions[idx1].subCategories.length - 1 && (
                        <Tooltip title={'Kompetenz nach Unten verschieben'}>
                          <ButtonBase className={classes.moveButton} onClick={moveCompetence('down', category.id, subCategory.id)}>
                            <ArrowDownwardIcon className={classes.icon} />
                          </ButtonBase>
                        </Tooltip>
                      )}
                    </Stack>
                  </Stack>
                }
              >
                <div className={classes.questions}>
                  {subCategory.questions.map((question, idx3) => (
                    <Stack
                      key={question.id}
                      className={classes.question}
                      direction={'row'}
                      alignItems={'center'}
                      justifyContent={'space-between'}
                      gap={2}
                    >
                      {/* name + selection */}
                      <PaiCheckbox
                        checked={selectedClosedQuestions.includes(question.id)}
                        onChange={handleSelectionClosedQuestionsChange(question.id)}
                        label={question.selfAssessment.de}
                      />
                      {/* delete + move actions */}
                      <Stack className={cx(classes.actions, 'actions')} direction={'row'} alignItems={'center'} gap={2} flexWrap={'nowrap'}>
                        <ButtonBase className={classes.deleteButton} onClick={handleOpenConfirmDeleteLeadQuestionDialog(question.id)}>
                          <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} gap={1}>
                            <DisabledByDefaultSharpIcon className={classes.icon} />
                            <Typography className={classes.text}>löschen</Typography>
                          </Stack>
                        </ButtonBase>
                        {idx3 !== 0 && (
                          <Tooltip title={'Frage nach Oben verschieben'}>
                            <ButtonBase className={classes.moveButton} onClick={moveClosedQuestion('up', category.id, subCategory.id, question.id)}>
                              <ArrowUpwardIcon className={classes.icon} />
                            </ButtonBase>
                          </Tooltip>
                        )}
                        {idx3 !== closedQuestions[idx1].subCategories[idx2].questions.length - 1 && (
                          <Tooltip title={'Frage nach Unten verschieben'}>
                            <ButtonBase className={classes.moveButton} onClick={moveClosedQuestion('down', category.id, subCategory.id, question.id)}>
                              <ArrowDownwardIcon className={classes.icon} />
                            </ButtonBase>
                          </Tooltip>
                        )}
                      </Stack>
                    </Stack>
                  ))}
                  <ButtonBase
                    className={classes.addButton}
                    onClick={handleOpenAddQuestionsDialog(subCategory.id, category.id, category.sourceId, subCategory.sourceId)}
                  >
                    <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} gap={1}>
                      <AddBoxSharpIcon className={classes.icon} />
                      <Typography className={classes.text}>Frage</Typography>
                    </Stack>
                  </ButtonBase>
                </div>
              </TreeItem>
            ))}
          </TreeItem>
        ))}
      </SimpleTreeView>
      {/* effect questions */}
      <SimpleTreeView expandedItems={expanded} onExpandedItemsChange={handleToggleTreeNode}>
        <TreeItem
          itemId={'effectQuestions'}
          label={
            <Stack direction={'row'} alignItems={'center'} gap={2}>
              <Typography className={classes.treeLabel}>Effektive Führung</Typography>
              <ButtonBase className={classes.addButton} onClick={handleOpenAddDimensionDialog()}>
                <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} gap={1}>
                  <AddBoxSharpIcon className={classes.icon} />
                  <Typography className={classes.text}>Dimension</Typography>
                </Stack>
              </ButtonBase>
            </Stack>
          }
          className={classes.treeItem}
        >
          {effectQuestions.map((subCategory, idx1) => (
            <TreeItem
              itemId={subCategory.id}
              key={subCategory.id}
              label={
                <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} gap={2}>
                  {/* name */}
                  <Typography className={classes.treeLabel}>{subCategory.name.de}</Typography>
                  {/* delete move actions */}
                  <Stack className={cx(classes.actions, 'actions')} direction={'row'} alignItems={'center'} gap={2} flexWrap={'nowrap'}>
                    <ButtonBase className={classes.deleteButton} onClick={handleOpenConfirmDeleteLeadCategoryDialog(subCategory.id)}>
                      <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} gap={1}>
                        <DisabledByDefaultSharpIcon className={classes.icon} />
                        <Typography className={classes.text}>löschen</Typography>
                      </Stack>
                    </ButtonBase>
                    {idx1 !== 0 && (
                      <Tooltip title={'Kategorie nach Oben verschieben'}>
                        <ButtonBase className={classes.moveButton} onClick={moveEffectCategory('up', subCategory.id)}>
                          <ArrowUpwardIcon className={classes.icon} />
                        </ButtonBase>
                      </Tooltip>
                    )}
                    {idx1 !== effectQuestions.length - 1 && (
                      <Tooltip title={'Kategorie nach Unten verschieben'}>
                        <ButtonBase className={classes.moveButton} onClick={moveEffectCategory('down', subCategory.id)}>
                          <ArrowDownwardIcon className={classes.icon} />
                        </ButtonBase>
                      </Tooltip>
                    )}
                  </Stack>
                </Stack>
              }
            >
              <div className={classes.questions}>
                {subCategory.questions.map((question, idx2) => (
                  <Stack
                    key={question.id}
                    className={classes.question}
                    direction={'row'}
                    alignItems={'center'}
                    justifyContent={'space-between'}
                    gap={2}
                  >
                    {/* name + selection */}
                    <PaiCheckbox
                      checked={selectedEffectQuestions.includes(question.id)}
                      onChange={handleSelectionEffectQuestionsChange(question.id)}
                      label={question.selfAssessment.de}
                    />
                    {/* delete + move actions */}
                    <Stack className={cx(classes.actions, 'actions')} direction={'row'} alignItems={'center'} gap={2} flexWrap={'nowrap'}>
                      <ButtonBase className={classes.deleteButton} onClick={handleOpenConfirmDeleteLeadQuestionDialog(question.id)}>
                        <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} gap={1}>
                          <DisabledByDefaultSharpIcon className={classes.icon} />
                          <Typography className={classes.text}>löschen</Typography>
                        </Stack>
                      </ButtonBase>
                      {idx2 !== 0 && (
                        <Tooltip title={'Frage nach Oben verschieben'}>
                          <ButtonBase className={classes.moveButton} onClick={moveEffectQuestion('up', subCategory.id, question.id)}>
                            <ArrowUpwardIcon className={classes.icon} />
                          </ButtonBase>
                        </Tooltip>
                      )}
                      {idx2 !== effectQuestions[idx1].questions.length - 1 && (
                        <Tooltip title={'Frage nach Unten verschieben'}>
                          <ButtonBase className={classes.moveButton} onClick={moveEffectQuestion('down', subCategory.id, question.id)}>
                            <ArrowDownwardIcon className={classes.icon} />
                          </ButtonBase>
                        </Tooltip>
                      )}
                    </Stack>
                  </Stack>
                ))}
                <ButtonBase
                  className={classes.addButton}
                  onClick={handleOpenAddQuestionsDialog(subCategory.id, undefined, undefined, subCategory.sourceId)}
                >
                  <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} gap={1}>
                    <AddBoxSharpIcon className={classes.icon} />
                    <Typography className={classes.text}>Frage</Typography>
                  </Stack>
                </ButtonBase>
              </div>
            </TreeItem>
          ))}
        </TreeItem>
      </SimpleTreeView>
      {/* open questions */}
      <SimpleTreeView className={classes.treeItem} expandedItems={expanded} onExpandedItemsChange={handleToggleTreeNode}>
        <TreeItem itemId={'openQuestions'} label={<Typography className={classes.treeLabel}>Ihre persönlichen Eindrücke</Typography>}>
          <div className={classes.questions}>
            {openQuestions.map((question, idx) => (
              <Stack key={question.id} className={classes.question} direction={'row'} alignItems={'center'} justifyContent={'space-between'} gap={2}>
                {/* name + selection */}
                <PaiCheckbox
                  checked={selectedOpenQuestions.includes(question.id)}
                  onChange={handleSelectionOpenQuestionsChange(question.id)}
                  label={question.selfAssessment.de}
                />
                {/* delete + move actions */}
                <Stack className={cx(classes.actions, 'actions')} direction={'row'} alignItems={'center'} gap={2} flexWrap={'nowrap'}>
                  <ButtonBase className={classes.deleteButton} onClick={handleOpenConfirmDeleteLeadQuestionDialog(question.id)}>
                    <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} gap={1}>
                      <DisabledByDefaultSharpIcon className={classes.icon} />
                      <Typography className={classes.text}>löschen</Typography>
                    </Stack>
                  </ButtonBase>
                  {idx !== 0 && (
                    <Tooltip title={'Frage nach Oben verschieben'}>
                      <ButtonBase className={classes.moveButton} onClick={moveOpenQuestion('up', question.id)}>
                        <ArrowUpwardIcon className={classes.icon} />
                      </ButtonBase>
                    </Tooltip>
                  )}
                  {idx !== openQuestions.length - 1 && (
                    <Tooltip title={'Frage nach Unten verschieben'}>
                      <ButtonBase className={classes.moveButton} onClick={moveOpenQuestion('down', question.id)}>
                        <ArrowDownwardIcon className={classes.icon} />
                      </ButtonBase>
                    </Tooltip>
                  )}
                </Stack>
              </Stack>
            ))}
            <ButtonBase className={classes.addButton} onClick={handleOpenAddNewOpenQuestionsDialog}>
              <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} gap={1}>
                <AddBoxSharpIcon className={classes.icon} />
                <Typography className={classes.text}>Frage</Typography>
              </Stack>
            </ButtonBase>
          </div>
        </TreeItem>
      </SimpleTreeView>
    </div>
  );
};

const useStyles = makeStyles()((theme) => ({
  questionnaire: {
    maxHeight: 'calc(100% - 19.5px - 32px)',
    width: '100%',
    padding: '16px',
    border: `2px solid ${theme.palette.backgrounds.main}`,
    overflow: 'auto',
  },
  treeItem: {
    color: theme.palette.greys.medium,
    '& .MuiTreeItem-content': {
      marginBottom: '8px',
      padding: '0 8px',
      '&.Mui-selected': {
        background: '#FFF',
      },
      '&.Mui-focused': {
        background: '#FFF',
      },
    },
    '& .MuiTreeItem-label': {
      fontSize: theme.typography.pxToRem(13),
    },
    '& .MuiCollapse-root': {
      marginLeft: '17px',
    },
    '& .MuiTreeItem-content:hover .actions': {
      visibility: 'visible',
    },
  },
  treeLabel: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.greys.medium,
  },
  questions: {
    padding: '0 0 8px 12px',
  },
  question: {
    paddingRight: '4px',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
    '&:hover .actions': {
      visibility: 'visible',
    },
  },
  actions: {
    visibility: 'hidden',
  },
  moveButton: {
    color: theme.palette.primary.main,
    '&:hover': {
      color: theme.palette.secondary.main,
    },
  },
  addButton: {
    color: theme.palette.primary.main,
    '&:hover': {
      color: theme.palette.secondary.main,
    },
  },
  deleteButton: {
    color: theme.palette.error.main,
    '&:hover': {
      color: theme.palette.secondary.main,
    },
  },
  icon: {
    fontSize: theme.typography.pxToRem(20),
  },
  text: {
    fontSize: theme.typography.pxToRem(13),
    textDecoration: 'underline',
    textUnderlineOffset: '4px',
  },
}));

export default LeadQuestionsCreation;
